/**
 * @file fraudService.js
 * @description Fraud detection service for agency signups. The duplicate check (same phone, same ZIP and a
 *   similar name, same IP) runs on the server in flag_agency_duplicate_signals, which writes admin_alerts.
 *   Never blocks signups; only flags for admin review.
 * @author Victor Carpinelli
 * @version 1.0.0
 * @module lib/fraudService
 */
import { supabase } from "./supabase";

/**
 * Check for duplicate signals after a new agency signs up.
 * Looks for: same phone, same ZIP + similar name, same IP.
 *
 * @param {object} params
 * @param {string} params.agencyId    — new agency's user ID
 * @param {string} params.agencyName  — agency name
 * @param {string} params.zip         — agency ZIP
 * @param {string} params.phone       — agency phone (optional)
 * @param {string} params.ip          — signup IP address (optional)
 */
export async function checkDuplicateSignals({ agencyName, zip, phone, ip }) {
  // Runs on the server (flag_agency_duplicate_signals): compares against other agencies there, writes
  // admin_alerts and the caller's fraud_signals, and returns only how many signals it found. The signup client
  // never receives other agencies' phones or names.
  const { data, error } = await supabase.rpc("flag_agency_duplicate_signals", {
    p_agency_name: agencyName || "", p_zip: zip || "", p_phone: phone || "", p_ip: ip || "",
  });
  if (error) { console.warn("[fraudService] checkDuplicateSignals failed:", error.message); return 0; }
  return data || 0;
}

/**
 * Load unresolved admin alerts (for AdminPanel).
 */
export async function loadAdminAlerts({ resolved = false, limit = 50 } = {}) {
  const { data, error } = await supabase
    .from("admin_alerts")
    .select("id, type, severity, message, data, agency_id, resolved, resolved_by, resolved_at, created_at")
    .eq("resolved", resolved)
    .order("created_at", { ascending: false })
    .limit(limit);
  if (error) throw error;
  return data || [];
}

/**
 * Resolve an admin alert (dismiss or flag).
 */
export async function resolveAdminAlert(alertId, userId, flagAccount = false) {
  const { error } = await supabase
    .from("admin_alerts")
    .update({ resolved: true, resolved_by: userId, resolved_at: new Date().toISOString() })
    .eq("id", alertId);
  if (error) throw error;

  // If flagging the account, mark it on agency_profiles
  if (flagAccount) {
    const { data: alert } = await supabase
      .from("admin_alerts")
      .select("agency_id")
      .eq("id", alertId)
      .maybeSingle();
    if (alert?.agency_id) {
      await supabase
        .from("agency_profiles")
        .update({ fraud_flagged: true })
        .eq("user_id", alert.agency_id);
    }
  }
}
